import { useState } from 'react';
import { useGameStore } from '../stores/gameStore';
import { useAuthStore } from '../stores/authStore';
import { useOnboardingStore } from '../stores/onboardingStore';
import { authApi } from '../services/api';
import PlayerStats from '../components/PlayerStats';
import IpChangePanel from '../components/IpChangePanel';
import FormatNodePanel from '../components/FormatNodePanel';

function apiMsg(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { message?: string } | string } };
  if (typeof e?.response?.data === 'string') return e.response.data;
  return e?.response?.data?.message ?? fallback;
}

// Lets the player set or change their account password (stored hashed server-side).
function ChangePasswordCard() {
  const [current, setCurrent]   = useState('');
  const [next, setNext]         = useState('');
  const [confirm, setConfirm]   = useState('');
  const [busy, setBusy]         = useState(false);
  const [msg, setMsg]           = useState<{ text: string; ok: boolean } | null>(null);

  const inputS: React.CSSProperties = {
    width: '100%', padding: '0.55rem 0.7rem', background: '#1a1a2e',
    border: '1px solid #333', borderRadius: '4px', color: '#e0e0e0',
    fontFamily: 'monospace', fontSize: '0.85rem', boxSizing: 'border-box',
  };
  const labelS: React.CSSProperties = {
    display: 'block', marginBottom: '0.35rem', fontSize: '0.75rem',
    color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em',
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next.length < 4) { setMsg({ text: 'Password must be at least 4 characters.', ok: false }); return; }
    if (next !== confirm) { setMsg({ text: 'New passwords do not match.', ok: false }); return; }
    setBusy(true); setMsg(null);
    try {
      await authApi.changePassword(next, current || undefined);
      setMsg({ text: 'Password updated.', ok: true });
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      setMsg({ text: apiMsg(err, 'Failed to update password.'), ok: false });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: '1.5rem' }}>
      <div className="card-header">
        <h2 className="card-title">Account Password</h2>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginBottom: '1rem', lineHeight: 1.5 }}>
        Protect your account so no one else can log in with your username. Leave
        “current password” blank if you haven’t set one yet.
      </p>
      <form onSubmit={submit} style={{ display: 'grid', gap: '0.8rem', maxWidth: '360px' }}>
        <div>
          <label style={labelS}>Current password</label>
          <input type="password" value={current} onChange={e => setCurrent(e.target.value)} style={inputS} autoComplete="current-password" />
        </div>
        <div>
          <label style={labelS}>New password</label>
          <input type="password" value={next} onChange={e => setNext(e.target.value)} style={inputS} autoComplete="new-password" />
        </div>
        <div>
          <label style={labelS}>Confirm new password</label>
          <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} style={inputS} autoComplete="new-password" />
        </div>
        {msg && <p style={{ margin: 0, fontSize: '0.85rem', color: msg.ok ? '#4ade80' : '#ef4444' }}>{msg.text}</p>}
        <button type="submit" className="primary" disabled={busy} style={{ justifySelf: 'start' }}>
          {busy ? 'Saving…' : 'Update password'}
        </button>
      </form>
    </div>
  );
}

export default function Profile() {
  const { player } = useGameStore();
  // CrazyGames sessions (account or guest) authenticate through the portal, not a
  // username/password — so there's no password to set/change there. Hide the panel.
  const authSource = useAuthStore(s => s.authSource);
  const showPasswordCard = authSource !== 'crazygames';

  return (
    <div>
      <h1 style={{ color: 'var(--primary-color)', marginBottom: '1.5rem' }}>
        Hacker Profile
      </h1>

      <PlayerStats />

      {player && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <div className="card-header">
            <h2 className="card-title">Account Information</h2>
          </div>
          
          <div style={{ display: 'grid', gap: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid var(--border-color)' }}>
              <span style={{ color: 'var(--text-muted)' }}>Username</span>
              <span>{player.username}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid var(--border-color)' }}>
              <span style={{ color: 'var(--text-muted)' }}>Member Since</span>
              <span>{new Date(player.createdAt).toLocaleDateString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0' }}>
              <span style={{ color: 'var(--text-muted)' }}>Last Login</span>
              <span>{new Date(player.lastLoginAt).toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderTop: '1px solid var(--border-color)', gap: '1rem', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text-muted)' }}>Tutorial</span>
              <button
                onClick={() => useOnboardingStore.getState().restart()}
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.8rem' }}
              >
                Replay tutorial
              </button>
            </div>
          </div>
        </div>
      )}

      <IpChangePanel />
      <FormatNodePanel />

      {showPasswordCard && <ChangePasswordCard />}
    </div>
  );
}
